/**
 * Storage Yard Resource Management Hooks
 *
 * React Query hooks for storage yard CRUD operations.
 * Handles caching, invalidation, and optimistic updates.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { storageYardService } from '../services/storage-yard.service';
import type { CreateStorageYardDTO, UpdateStorageYardDTO } from '../schemas/storageYardSchema';

const STORAGE_YARDS_KEY = ['storage-yards'] as const;

/**
 * Hook to fetch storage yards list
 */
export function useStorageYards() {
  return useQuery({
    queryKey: STORAGE_YARDS_KEY,
    queryFn: () => storageYardService.list(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to fetch a single storage yard by ID
 */
export function useStorageYard(id: string | undefined) {
  return useQuery({
    queryKey: [...STORAGE_YARDS_KEY, id],
    queryFn: () => storageYardService.getById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook to fetch containers in a specific yard
 */
export function useYardContainers(yardId: string | undefined) {
  return useQuery({
    queryKey: [...STORAGE_YARDS_KEY, yardId, 'containers'],
    queryFn: () => storageYardService.getYardContainers(yardId!),
    enabled: !!yardId,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook to create a new storage yard
 */
export function useCreateStorageYard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateStorageYardDTO) => storageYardService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORAGE_YARDS_KEY });
      toast.success('Storage yard created successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create storage yard');
    },
  });
}

/**
 * Hook to update storage yard details
 */
export function useUpdateStorageYard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStorageYardDTO }) =>
      storageYardService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORAGE_YARDS_KEY });
      toast.success('Storage yard updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update storage yard');
    },
  });
}

/**
 * Hook to delete storage yard
 */
export function useDeleteStorageYard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => storageYardService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORAGE_YARDS_KEY });
      toast.success('Storage yard deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete storage yard');
    },
  });
}
